import { useEffect, useLayoutEffect, useRef } from "react";
import type {
  AgentWorkspaceDefinition,
  AvailableToolDefinition,
  GitRepositoryDefinition,
  McpServerDefinition,
  RepositoryContextDefinition,
  SkillDefinition,
} from "@lumen/shared";
import { api } from "../../api";
import { loadArtifactReferences } from "../artifacts/loadArtifactReferences";
import { t } from "../../i18n";
import { generationParameterFields } from "./ComposerParameterDefinitions";
import { getComposerPopupPosition } from "./ComposerPopupPosition";
import type {
  ComposerDerivedContext,
  ComposerReference,
  WorkspaceFileReference,
} from "./ComposerContextTypes";

const isContainerWorkspaceType = (type: string) =>
  ["container", "process-sandbox", "rootless-podman"].includes(type);

type PersonalizationPreferences = {
  defaultWorkspaceId?: string | null;
  defaultRepositoryId?: string | null;
  mcpServerIds: string[];
  toolIds: string[];
  skillIds: string[];
};

export function useComposerDerivedState(context: ComposerDerivedContext) {
  const workspaceFilesGeneration = useRef(0);
  const {
    activity,
    activityOpen,
    activityPicker,
    agents,
    agentId,
    agentOpen,
    agentPicker,
    agentWorkspaceId,
    agentWorkspaceIds,
    agentWorkspaces,
    agentWorkspacesLoaded,
    artifacts,
    artifactDismissed,
    workspaceFiles,
    workspaceFilesStatus,
    conversationId,
    developmentConfigured,
    developmentWorkspaceLoaded,
    enabledActivities,
    generationParameters,
    initialOptionsRef,
    persistSelections,
    mcpServerIds,
    mcpToolSelections,
    mode,
    onEnvironmentConfigured,
    panelSection,
    paramsButton,
    paramsOpen,
    paramsPopup,
    preferencesLoaded,
    repositoryId,
    repositoryIds,
    repositories,
    richInput,
    setActivityOpen,
    setAgentOpen,
    setAgentWorkspaceId,
    setAgentWorkspaceIds,
    setAgentWorkspaces,
    setAgentWorkspacesLoaded,
    setArtifacts,
    setArtifactsStatus,
    setWorkspaceFiles,
    setWorkspaceFilesStatus,
    setMcpServerIds,
    setMcpServers,
    setParamsMenuPosition,
    setParamsOpen,
    setPreferredSkillIds,
    setRepositoryId,
    setRepositoryIds,
    setRepositories,
    setSkills,
    setSkillsStatus,
    setRepositoryDiagnostics,
    setToolIds,
    setToolMenuPosition,
    setTools,
    setSkillMenuPosition,
    setArtifactMenuPosition,
    setToolsOpen,
    setSettingsToolId,
    skills,
    slashDismissed,
    caretText,
    supportedParameters,
    thinkingLevel,
    toolButton,
    toolIds,
    toolPopup,
    toolsOpen,
    value,
  } = context;
  const safeEnabledActivities = Array.isArray(enabledActivities)
    ? enabledActivities
    : ["ask"];
  const safeMcpServerIds = Array.isArray(mcpServerIds) ? mcpServerIds : [];
  const safeToolIds = Array.isArray(toolIds) ? toolIds : [];
  const safeSupportedParameters = Array.isArray(supportedParameters)
    ? supportedParameters
    : [];
  useEffect(() => {
    api<PersonalizationPreferences>("/me/personalization-preferences")
      .then((preferences) => {
        const safePreferences = {
          ...preferences,
          mcpServerIds: Array.isArray(preferences?.mcpServerIds)
            ? preferences.mcpServerIds
            : [],
          toolIds: Array.isArray(preferences?.toolIds)
            ? preferences.toolIds
            : [],
          skillIds: Array.isArray(preferences?.skillIds)
            ? preferences.skillIds
            : [],
        };
        const initial = initialOptionsRef.current;
        if (
          initial?.agentWorkspaceId === undefined &&
          !localStorage.getItem("lumen:active-agent-workspace")
        )
          setAgentWorkspaceIds(
            safePreferences.defaultWorkspaceId
              ? [safePreferences.defaultWorkspaceId]
              : [],
          );
        if (
          initial?.repositoryId === undefined &&
          !localStorage.getItem("lumen:active-repository")
        )
          setRepositoryIds((current) => ({
            ...current,
            ...(safePreferences.defaultWorkspaceId
              ? {
                  [safePreferences.defaultWorkspaceId]:
                    safePreferences.defaultRepositoryId
                      ? [safePreferences.defaultRepositoryId]
                      : [],
                }
              : {}),
          }));
        if (
          initial?.mcpServerIds === undefined &&
          !localStorage.getItem("lumen:active-mcps")
        )
          setMcpServerIds(safePreferences.mcpServerIds);
        if (
          initial?.toolIds === undefined &&
          !localStorage.getItem("lumen:active-tools")
        )
          setToolIds(safePreferences.toolIds);
        if (initial?.skillIds === undefined)
          setPreferredSkillIds(safePreferences.skillIds);
        preferencesLoaded.current = true;
      })
      .catch(() => {
        preferencesLoaded.current = true;
      });
    api<SkillDefinition[]>("/skills")
      .then((response) => {
        const rows = Array.isArray(response) ? response : [];
        setSkills((current) => [
          ...rows.filter((row) => row.enabled),
          ...current.filter(
            (skill) => "source" in skill && skill.source === "repository",
          ),
        ]);
        setSkillsStatus("ready");
      })
      .catch(() => setSkillsStatus("error"));
    api<McpServerDefinition[]>("/mcp-servers/available")
      .then((response) => {
        const rows = Array.isArray(response) ? response : [];
        setMcpServers(rows);
        if (rows.length)
          setMcpServerIds((current) =>
            current.filter((id) => rows.some((server) => server.id === id)),
          );
      })
      .catch(() => undefined);
    api<AgentWorkspaceDefinition[]>("/agent-workspaces/available")
      .then((response) => {
        const rows = Array.isArray(response) ? response : [];
        setAgentWorkspaces(rows);
        setAgentWorkspacesLoaded(true);
        setAgentWorkspaceIds((current) =>
          current.filter((id) =>
            rows.some(
              (workspace) =>
                workspace.id === id && workspace.available !== false,
            ),
          ),
        );
      })
      .catch(() => setAgentWorkspacesLoaded(true));
    api<GitRepositoryDefinition[]>("/git-repositories")
      .then((response) => {
        const rows = Array.isArray(response) ? response : [];
        setRepositories(rows.filter((row) => row.enabled));
        setRepositoryIds((current) =>
          Object.fromEntries(
            (Object.entries(current) as Array<[string, string[]]>).map(
              ([workspaceId, ids]) => [
                workspaceId,
                ids.filter((id) =>
                  rows.some(
                    (repository) =>
                      repository.id === id && repository.enabled !== false,
                  ),
                ),
              ],
            ),
          ),
        );
      })
      .catch(() => undefined);
    api<AvailableToolDefinition[]>("/tools/available")
      .then((response) => {
        const rows = Array.isArray(response) ? response : [];
        setTools(rows);
        if (rows.length)
          setToolIds((current) =>
            current.filter((id) => rows.some((tool) => tool.id === id)),
          );
      })
      .catch(() => undefined);
  }, [
    initialOptionsRef,
    preferencesLoaded,
    setAgentWorkspaceIds,
    setAgentWorkspaces,
    setAgentWorkspacesLoaded,
    setMcpServerIds,
    setMcpServers,
    setPreferredSkillIds,
    setRepositories,
    setRepositoryIds,
    setRepositoryDiagnostics,
    setSkills,
    setSkillsStatus,
    setToolIds,
    setTools,
  ]);
  useEffect(() => {
    if (mode === "send" && persistSelections && preferencesLoaded.current)
      localStorage.setItem("lumen:active-tools", JSON.stringify(toolIds));
  }, [mode, persistSelections, preferencesLoaded, toolIds]);
  useEffect(() => {
    if (mode === "send" && persistSelections && preferencesLoaded.current)
      localStorage.setItem("lumen:active-mcps", JSON.stringify(mcpServerIds));
  }, [mcpServerIds, mode, persistSelections, preferencesLoaded]);
  useEffect(() => {
    if (mode === "send" && persistSelections)
      localStorage.setItem(
        "lumen:mcp-tool-selections",
        JSON.stringify(mcpToolSelections),
      );
  }, [mcpToolSelections, mode, persistSelections]);
  // Workspace and repository state is initialized from these values in
  // Composer and synchronized there when a new conversation/options snapshot
  // arrives. Rehydrating it from localStorage here on derived-state changes
  // overwrites selections made in the environment popup (notably in Project
  // conversations, where selecting a workspace can trigger a parent render).
  useEffect(() => {
    if (mode !== "send" || !persistSelections) return;
    localStorage.setItem(
      "lumen:active-agent-workspace",
      agentWorkspaceId ?? "",
    );
  }, [agentWorkspaceId, mode, persistSelections]);
  useEffect(() => {
    if (mode !== "send" || !persistSelections) return;
    localStorage.setItem("lumen:active-repository", repositoryId ?? "");
  }, [mode, persistSelections, repositoryId]);
  useEffect(() => {
    if (mode !== "send" || !persistSelections) return;
    localStorage.setItem(
      "lumen:active-agent-workspaces",
      JSON.stringify(agentWorkspaceIds),
    );
  }, [agentWorkspaceIds, mode, persistSelections]);
  useEffect(() => {
    if (mode !== "send" || !persistSelections) return;
    localStorage.setItem(
      "lumen:active-repositories",
      JSON.stringify(repositoryIds),
    );
  }, [mode, persistSelections, repositoryIds]);
  useEffect(() => {
    setAgentWorkspaceId(
      agentWorkspaceIds.length === 1 ? agentWorkspaceIds[0] : null,
    );
    setRepositoryId(
      agentWorkspaceIds.length === 1
        ? ((repositoryIds[agentWorkspaceIds[0]] ?? [])[0] ?? null)
        : null,
    );
  }, [agentWorkspaceIds, repositoryIds, setAgentWorkspaceId, setRepositoryId]);
  useEffect(() => {
    if (mode !== "send" || !persistSelections) return;
    localStorage.setItem(
      "lumen:composer-generation-parameters",
      JSON.stringify(generationParameters),
    );
  }, [generationParameters, mode, persistSelections]);
  const selectedWorkspace = (
    Array.isArray(agentWorkspaces) ? agentWorkspaces : []
  ).find(
    (workspace) =>
      agentWorkspaceIds.length === 1 && workspace.id === agentWorkspaceIds[0],
  );
  const selectedRepository = (
    Array.isArray(repositories) ? repositories : []
  ).find(
    (repository) =>
      agentWorkspaceIds.length === 1 &&
      (repositoryIds[agentWorkspaceIds[0]] ?? []).length === 1 &&
      repository.id === repositoryIds[agentWorkspaceIds[0]][0],
  );
  const selectedRepositoryCount = (
    Object.values(repositoryIds) as string[][]
  ).reduce((total, ids) => total + ids.length, 0);
  const selectedWorkspaceLabel = selectedWorkspace
    ? selectedWorkspace.name
    : agentWorkspaceIds.length > 0
      ? `${agentWorkspaceIds.length} workspaces`
      : null;
  const selectedRepositoryLabel = selectedRepository
    ? selectedRepository.name
    : selectedRepositoryCount > 0
      ? `${selectedRepositoryCount} repositories`
      : null;
  const selectedAgent = (Array.isArray(agents) ? agents : []).find(
    (agent) => agent.id === agentId,
  );
  const activityLabel =
    activity === "image"
      ? t("Create image")
      : activity === "audio"
        ? t("Generate audio")
        : activity === "video"
          ? t("Create video")
          : activity === "speech"
            ? t("Generate speech")
            : activity === "transcription"
              ? t("Transcribe")
              : activity === "model3d"
                ? t("Create 3D model")
                : activity === "sprite"
                  ? t("Create sprite")
                  : activity === "tileset"
                    ? t("Create tileset")
                    : t("Ask");
  const activeToolCount =
    safeToolIds.length +
    safeMcpServerIds.length +
    safeEnabledActivities.filter((item) => item !== "ask").length;
  const thinkingFill =
    thinkingLevel === "high" || thinkingLevel === "max"
      ? "100%"
      : thinkingLevel === "medium"
        ? "66.666%"
        : thinkingLevel === "low"
          ? "33.333%"
          : "0%";
  const availableParameterFields = safeSupportedParameters.length
    ? generationParameterFields.filter((field) =>
        field.providerKeys.some((key: string) =>
          safeSupportedParameters.includes(key),
        ),
      )
    : generationParameterFields;
  const advancedParameterFields = availableParameterFields.filter(
    (field) => !["temperature", "topP", "seed"].includes(field.key),
  );
  const activeParameterCount =
    availableParameterFields.filter(
      (field) => typeof generationParameters[field.key] === "number",
    ).length +
    (generationParameters.goal === true ? 1 : 0) +
    ((activity === "audio" || activity === "speech") &&
    generationParameters.voice
      ? 1
      : 0) +
    ((activity === "audio" || activity === "speech") &&
    generationParameters.format
      ? 1
      : 0);
  useEffect(() => {
    if (!activityOpen) return;
    const close = (event: PointerEvent) => {
      if (!activityPicker.current?.contains(event.target as Node))
        setActivityOpen(false);
    };
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setActivityOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [activityOpen, activityPicker, setActivityOpen]);
  useEffect(() => {
    if (!agentWorkspacesLoaded) return;
    const containerIds = new Set(
      agentWorkspaces
        .filter((workspace) => isContainerWorkspaceType(workspace.type))
        .map((workspace) => workspace.id),
    );
    setRepositoryIds((current) =>
      Object.fromEntries(
        Object.entries(current).map(([workspaceId, repositoryId]) => [
          workspaceId,
          containerIds.has(workspaceId) ? repositoryId : [],
        ]),
      ),
    );
  }, [agentWorkspaces, agentWorkspacesLoaded, setRepositoryIds]);
  useEffect(() => {
    setSkills((current) =>
      current.filter(
        (skill) => !("source" in skill && skill.source === "repository"),
      ),
    );
    const hasRepositorySelection = (
      Object.values(repositoryIds) as string[][]
    ).some((ids) => ids.length > 0);
    const hasMachineSelection =
      selectedWorkspace?.type === "machine" && agentWorkspaceIds.length === 1;
    if (
      !conversationId ||
      !developmentWorkspaceLoaded ||
      !developmentConfigured ||
      (!hasRepositorySelection && !hasMachineSelection)
    ) {
      setRepositoryDiagnostics([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void api<RepositoryContextDefinition>(
        `/conversations/${conversationId}/repository-context`,
      )
        .then((context) => {
          setSkills((current) => [
            ...current.filter(
              (skill) => !("source" in skill && skill.source === "repository"),
            ),
            ...context.skills,
          ]);
          setRepositoryDiagnostics(
            Array.isArray(context.diagnostics) ? context.diagnostics : [],
          );
        })
        .catch(() => setRepositoryDiagnostics([]));
    }, 500);
    return () => window.clearTimeout(timer);
  }, [
    agentWorkspaceIds.length,
    conversationId,
    developmentConfigured,
    developmentWorkspaceLoaded,
    repositoryId,
    repositoryIds,
    selectedWorkspace?.type,
    setRepositoryDiagnostics,
    setSkills,
  ]);
  useEffect(() => {
    if (
      !conversationId ||
      ![
        "container",
        "process-sandbox",
        "rootless-podman",
        "machine",
        "ssh",
        "proxmox",
      ].includes(selectedWorkspace?.type ?? "") ||
      agentWorkspaceIds.length > 1 ||
      !developmentWorkspaceLoaded
    )
      return;
    if (!selectedWorkspace) return;
    const selectedRepositoryIds = repositoryIds[selectedWorkspace.id] ?? [];
    const timer = window.setTimeout(() => {
      void api(`/conversations/${conversationId}/development-workspace`, {
        method: "PUT",
        body: JSON.stringify({
          workspaceId: selectedWorkspace.id,
          ...(selectedRepositoryIds.length > 1
            ? { repositoryIds: selectedRepositoryIds }
            : { repositoryId: selectedRepositoryIds[0] ?? repositoryId }),
        }),
      })
        .then(() => onEnvironmentConfigured?.(true))
        .catch(() => undefined);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [
    conversationId,
    developmentWorkspaceLoaded,
    agentWorkspaceIds,
    onEnvironmentConfigured,
    repositoryIds,
    repositoryId,
    selectedWorkspace,
  ]);
  useLayoutEffect(() => {
    if (!toolsOpen || !toolButton.current) {
      return;
    }
    const update = () => {
      const button = toolButton.current;
      if (!button) return;
      setToolMenuPosition(getComposerPopupPosition(button, 544));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [panelSection, setToolMenuPosition, toolButton, toolsOpen]);
  useLayoutEffect(() => {
    if (!paramsOpen || !paramsButton.current) {
      return;
    }
    const update = () => {
      const button = paramsButton.current;
      if (!button) return;
      setParamsMenuPosition(getComposerPopupPosition(button, 320));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [paramsButton, paramsOpen, setParamsMenuPosition]);
  useEffect(() => {
    if (!paramsOpen) return;
    const close = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !paramsButton.current?.contains(target) &&
        !paramsPopup.current?.contains(target)
      ) {
        setParamsOpen(false);
      }
    };
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setParamsOpen(false);
    };
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", close, true);
      document.removeEventListener("keydown", escape);
    };
  }, [paramsButton, paramsOpen, paramsPopup, setParamsOpen]);
  useEffect(() => {
    if (!toolsOpen) return;
    const close = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !toolButton.current?.contains(target) &&
        !toolPopup.current?.contains(target)
      ) {
        setToolsOpen(false);
        setSettingsToolId(null);
      }
    };
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setToolsOpen(false);
        setSettingsToolId(null);
      }
    };
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", close, true);
      document.removeEventListener("keydown", escape);
    };
  }, [setSettingsToolId, setToolsOpen, toolButton, toolPopup, toolsOpen]);
  useEffect(() => {
    if (!agentOpen) return;
    const close = (event: PointerEvent) => {
      if (!agentPicker.current?.contains(event.target as Node))
        setAgentOpen(false);
    };
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setAgentOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [agentOpen, agentPicker, setAgentOpen]);
  const slash = /(?:^|\s)\/([a-z0-9-]*)$/i.exec(caretText);
  const slashOpen = Boolean(slash) && !slashDismissed;
  const skillMatches = slash
    ? skills
        .filter(
          (skill) =>
            skill.slug.includes(slash[1].toLowerCase()) ||
            skill.name.toLowerCase().includes(slash[1].toLowerCase()),
        )
        .slice(0, 8)
    : [];
  const artifactTrigger = /(^|\s)@(?:(artifact:?)?)([a-z0-9._~/-]*)$/i.exec(
    caretText,
  );
  const artifactOpen = Boolean(artifactTrigger) && !artifactDismissed;
  const artifactPrefix = artifactTrigger?.[2]?.toLowerCase() ?? "";
  const artifactQuery = artifactTrigger?.[3]?.toLowerCase() ?? "";
  const artifactMatches =
    artifactTrigger && !artifactPrefix.startsWith("workspace")
      ? artifacts
          .filter(
            (artifact) =>
              artifact.name.toLowerCase().includes(artifactQuery) ||
              artifact.id.toLowerCase().includes(artifactQuery) ||
              artifact.reference.toLowerCase().includes(artifactQuery),
          )
          .slice(0, 8)
      : [];
  const workspaceFileMatches =
    artifactTrigger && !artifactPrefix ? workspaceFiles.slice(0, 8) : [];
  const referenceMatches: ComposerReference[] = [
    ...artifactMatches.map((item) => ({ kind: "artifact" as const, item })),
    ...workspaceFileMatches.map((item) => ({
      kind: "workspace-file" as const,
      item,
    })),
  ];
  useEffect(() => {
    let active = true;
    setArtifacts([]);
    setArtifactsStatus("loading");
    loadArtifactReferences(conversationId)
      .then((rows) => {
        if (!active) return;
        setArtifacts(rows);
        setArtifactsStatus("ready");
      })
      .catch(() => {
        if (active) setArtifactsStatus("error");
      });
    return () => {
      active = false;
    };
  }, [artifactOpen, conversationId, setArtifacts, setArtifactsStatus]);
  useEffect(() => {
    if (!artifactOpen || artifactPrefix) {
      setWorkspaceFiles([]);
      setWorkspaceFilesStatus("ready");
      return;
    }
    // Existing conversation: conversation-linked workspaces and repositories.
    if (conversationId) {
      const timer = window.setTimeout(() => {
        setWorkspaceFilesStatus("loading");
        api<WorkspaceFileReference[]>(
          `/conversations/${conversationId}/workspace-files?query=${encodeURIComponent(artifactQuery)}`,
        )
          .then((rows) => {
            setWorkspaceFiles(Array.isArray(rows) ? rows : []);
            setWorkspaceFilesStatus("ready");
          })
          .catch(() => setWorkspaceFilesStatus("error"));
      }, 150);
      return () => window.clearTimeout(timer);
    }
    // No conversation yet (new chat): machine workspaces already hold files,
    // so list references from every selected machine workspace. Sources
    // resolve independently and merge incrementally, so one slow connector
    // cannot block the others, and stale responses are dropped by generation.
    const machineWorkspaces = agentWorkspaces.filter(
      (workspace) =>
        workspace.type === "machine" &&
        agentWorkspaceIds.includes(workspace.id),
    );
    if (!machineWorkspaces.length) {
      setWorkspaceFiles([]);
      setWorkspaceFilesStatus("ready");
      return;
    }
    const generation = ++workspaceFilesGeneration.current;
    let pending = machineWorkspaces.length;
    let failures = 0;
    const merged = new Map<string, WorkspaceFileReference>();
    const flush = () => {
      if (workspaceFilesGeneration.current !== generation) return;
      setWorkspaceFiles([...merged.values()]);
      setWorkspaceFilesStatus(
        pending === 0 && failures === machineWorkspaces.length
          ? "error"
          : "ready",
      );
    };
    const timer = window.setTimeout(() => {
      for (const workspace of machineWorkspaces) {
        api<WorkspaceFileReference[]>(
          `/agent-workspaces/${workspace.id}/workspace-files?query=${encodeURIComponent(artifactQuery)}`,
        )
          .then((rows) => {
            if (workspaceFilesGeneration.current !== generation) return;
            for (const row of Array.isArray(rows) ? rows : []) {
              merged.set(
                `${row.workspaceSlug}:${row.repositorySlug ?? ""}:${row.path}`,
                row,
              );
            }
          })
          .catch(() => {
            failures += 1;
          })
          .finally(() => {
            pending -= 1;
            flush();
          });
      }
    }, 150);
    return () => window.clearTimeout(timer);
  }, [
    artifactOpen,
    artifactPrefix,
    artifactQuery,
    conversationId,
    agentWorkspaces,
    agentWorkspaceIds,
    setWorkspaceFiles,
    setWorkspaceFilesStatus,
  ]);
  const command = /(?:^|\s)\/([a-z0-9-]+)(?=\s|$)/i.exec(value);
  const selectedSkill = command
    ? [...skills]
        .reverse()
        .find((skill) => skill.slug === command[1].toLowerCase())
    : undefined;
  useLayoutEffect(() => {
    const updateSuggestions = () => {
      const input = richInput.current;
      const element = input?.getElement();
      if (!element || (!slashOpen && !artifactOpen)) {
        setSkillMenuPosition(null);
        setArtifactMenuPosition(null);
        return;
      }
      const rect = input?.getCaretRect() ?? element.getBoundingClientRect();
      const width = Math.min(slashOpen ? 384 : 420, window.innerWidth - 16);
      const below = window.innerHeight - rect.bottom - 12;
      const above = Math.min(256, Math.max(72, rect.top - 12));
      const position = {
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        top: below >= 256 ? rect.bottom + 4 : Math.max(above + 8, rect.top - 4),
        transform: below >= 256 ? undefined : "translateY(-100%)",
        width,
        maxHeight: below >= 256 ? 256 : above,
      };
      setSkillMenuPosition(slashOpen ? position : null);
      setArtifactMenuPosition(artifactOpen ? position : null);
    };
    updateSuggestions();
    window.addEventListener("resize", updateSuggestions);
    window.addEventListener("scroll", updateSuggestions, true);
    return () => {
      window.removeEventListener("resize", updateSuggestions);
      window.removeEventListener("scroll", updateSuggestions, true);
    };
  }, [
    artifactOpen,
    slashOpen,
    richInput,
    setArtifactMenuPosition,
    setSkillMenuPosition,
    value,
    caretText,
  ]);
  return {
    selectedWorkspace,
    selectedRepository,
    selectedRepositoryCount,
    selectedWorkspaceLabel,
    selectedRepositoryLabel,
    selectedAgent,
    activityLabel,
    activeToolCount,
    thinkingFill,
    availableParameterFields,
    advancedParameterFields,
    activeParameterCount,
    slash,
    slashOpen,
    skillMatches,
    artifactTrigger,
    artifactOpen,
    artifactQuery,
    artifactMatches,
    referenceMatches,
    workspaceFileMatches,
    workspaceFilesStatus,
    command,
    selectedSkill,
  };
}
